import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { RoleDto } from '@asset-metrix/portal-gateway-client-js';
import { Button, Post } from '@asset-metrix/portal-ui';
import { useShallow } from 'zustand/react/shallow';

import { useUserRolesData } from '../../api/portalGateway/role';
import { useStore } from '../../store';
import { FullScreenFrame } from '../fullscreenframe/FullScreenFrame';

const NoRolesError = () => {
  const { t } = useTranslation();
  const logoutSession = useStore((state) => state.logoutSession);

  const logout = useCallback(async () => {
    await logoutSession({ initiatedByUser: true });
  }, [logoutSession]);

  return (
    <FullScreenFrame>
      <Post>
        <h1>{t('app.noAccessError.headline')}</h1>
        <p>{t('app.noAccessError.message')}</p>
        <p>{t('app.noAccessError.resolution')}</p>
        <p data-right>
          <Button onClick={() => logout()}>
            {t('app.noAccessError.logoutButton')}
          </Button>
        </p>
      </Post>
    </FullScreenFrame>
  );
};

export function UserRolesLoader() {
  const navigate = useNavigate();
  const { data: userRolesData } = useUserRolesData();
  const userRoles = userRolesData.items;

  const { roleId } = useParams();
  const [activeUserRole, setActiveUserRole] = useStore(
    useShallow((state) => [state.activeUserRole, state.setActiveUserRole])
  );

  const defaultUserRole = userRoles[0];
  let roleFromUrl: RoleDto | undefined;

  if (roleId != null) {
    const decodedRoleId = decodeURIComponent(roleId);
    roleFromUrl = userRoles.find((r) => r.id === decodedRoleId);
  }

  useEffect(() => {
    if (roleFromUrl == null) {
      if (defaultUserRole && !activeUserRole) {
        navigate(`/p/${encodeURIComponent(defaultUserRole.id)}`);
      }
    } else {
      if (activeUserRole?.id !== roleFromUrl.id) {
        setActiveUserRole(roleFromUrl);
      }
    }
  }, [
    activeUserRole,
    roleFromUrl,
    defaultUserRole,
    navigate,
    setActiveUserRole,
  ]);

  if (userRoles.length === 0) {
    return <NoRolesError />;
  }

  if (activeUserRole) {
    return <Outlet />;
  }
}
